import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  guardianCapabilityNames,
  guardianRelationships,
  type AdultTransitionView,
  type AthleteView,
  type CreatedInvitationResponse,
  type GuardianCapabilityName,
  type GuardianCandidateView,
  type GuardianLinkView,
  type PageResponse,
  type SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import { athleteDisplayName, formatCalendarDate } from './athletes';
import {
  availableDecisions,
  canSubmitGuardianLink,
  emptyGuardianLinkForm,
  guardianCapabilityKeys,
  guardianLinkFilterKeys,
  guardianLinkFilters,
  guardianLinkListPath,
  guardianLinkRequestBody,
  guardianLinkStatusKeys,
  guardianRelationshipKeys,
  withAthlete,
  withoutAthlete,
  type GuardianLinkDecisionName,
  type GuardianLinkFilter,
  type GuardianLinkForm,
} from './guardianLinks';
import { createdInvitationNotice, type CreatedInvitationNotice } from './invitations';
import { InvitationLinkNotice } from './InvitationLinkNotice';
import { RemoteSelect, type RemoteOption } from './RemoteSelect';

export interface GuardianLinksPanelProps {
  locale: SupportedLocale;
  organizationId: string;
  csrfToken: string;
}

type ErrorKey = ApiRequestError['messageKey'];

function errorKeyOf(error: unknown): ErrorKey {
  return error instanceof ApiRequestError ? error.messageKey : 'error.unexpected';
}

const decisionLabelKeys = {
  approve: 'guardianLinks.approve',
  reject: 'guardianLinks.reject',
  revoke: 'guardianLinks.revoke',
} as const;

export function GuardianLinksPanel({
  locale,
  organizationId,
  csrfToken,
}: GuardianLinksPanelProps): React.JSX.Element {
  const [links, setLinks] = useState<GuardianLinkView[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [filter, setFilter] = useState<GuardianLinkFilter>('pending');
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [saved, setSaved] = useState(false);
  const [confirmingRevoke, setConfirmingRevoke] = useState<string | null>(null);
  const [transitionsKey, setTransitionsKey] = useState(0);
  const [form, setForm] = useState<GuardianLinkForm>(emptyGuardianLinkForm);
  // Changing the key empties the pickers after a request was stored.
  const [formKey, setFormKey] = useState(0);
  // Changing this key empties the athlete picker after each choice, ready for the next athlete.
  const [athletePickerKey, setAthletePickerKey] = useState(0);
  const base = `/api/v1/organizations/${organizationId}`;

  const load = useCallback(
    async (status: GuardianLinkFilter, cursor: string | null) => {
      try {
        const page = await apiRequest<PageResponse<GuardianLinkView>>(
          guardianLinkListPath(base, { status, cursor }),
        );
        setLinks((current) => (cursor === null ? page.items : [...(current ?? []), ...page.items]));
        setNextCursor(page.nextCursor);
      } catch (error) {
        setErrorKey(errorKeyOf(error));
      }
    },
    [base],
  );

  useEffect(() => {
    void load(filter, null);
  }, [load, filter]);

  const findAthletes = useCallback(
    async (text: string): Promise<RemoteOption[]> => {
      const page = await apiRequest<PageResponse<AthleteView>>(
        `${base}/athletes?limit=20&q=${encodeURIComponent(text)}`,
      );
      return page.items.map((athlete) => ({
        id: athlete.id,
        label: `${athleteDisplayName(athlete, locale)} (${formatCalendarDate(athlete.dateOfBirth, locale)})`,
      }));
    },
    [base, locale],
  );

  const findGuardians = useCallback(
    async (text: string): Promise<RemoteOption[]> => {
      const candidates = await apiRequest<GuardianCandidateView[]>(
        `${base}/guardian-links/guardians?q=${encodeURIComponent(text)}`,
      );
      return candidates.map((person) => ({
        id: person.personId,
        label: athleteDisplayName(person, locale),
      }));
    },
    [base, locale],
  );

  async function decide(link: GuardianLinkView, decision: GuardianLinkDecisionName): Promise<void> {
    setErrorKey(null);
    setSaved(false);
    setConfirmingRevoke(null);
    try {
      await apiRequest<GuardianLinkView>(`${base}/guardian-links/${link.id}/${decision}`, {
        method: 'POST',
        body: {},
        csrfToken,
        ifMatchVersion: link.version,
      });
      setSaved(true);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
    // Reload either way: after a conflict the list must show the link's current state.
    await load(filter, null);
  }

  async function authorizeAdult(link: GuardianLinkView): Promise<void> {
    setErrorKey(null);
    setSaved(false);
    try {
      await apiRequest<GuardianLinkView>(`${base}/guardian-links/${link.id}/authorize-adult`, {
        method: 'POST',
        body: {},
        csrfToken,
        ifMatchVersion: link.version,
      });
      setSaved(true);
      setTransitionsKey((current) => current + 1);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
    await load(filter, null);
  }

  async function updateLink(
    link: GuardianLinkView,
    capabilities: Record<GuardianCapabilityName, boolean>,
    validUntil: string,
  ): Promise<void> {
    setErrorKey(null);
    setSaved(false);
    try {
      await apiRequest<GuardianLinkView>(`${base}/guardian-links/${link.id}`, {
        method: 'PATCH',
        body: { capabilities, validUntil: validUntil === '' ? null : validUntil },
        csrfToken,
        ifMatchVersion: link.version,
      });
      setSaved(true);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
    await load(filter, null);
  }

  /** One link per chosen athlete; with "approve now" each is approved as soon as it exists. */
  async function request(): Promise<void> {
    setErrorKey(null);
    setSaved(false);
    let remaining = form;
    let createdGuardianPersonId: string | null = null;
    try {
      for (const athlete of form.athletes) {
        const created: GuardianLinkView = await apiRequest<GuardianLinkView>(
          `${base}/guardian-links`,
          {
            method: 'POST',
            body: guardianLinkRequestBody(form, athlete.id, createdGuardianPersonId),
            csrfToken,
          },
        );
        remaining = withoutAthlete(remaining, athlete.id);
        if (form.guardianSource === 'new') createdGuardianPersonId = created.guardianPersonId;
        if (form.approveNow) {
          await apiRequest<GuardianLinkView>(`${base}/guardian-links/${created.id}/approve`, {
            method: 'POST',
            body: {},
            csrfToken,
            ifMatchVersion: created.version,
          });
        }
      }
      setSaved(true);
      setForm(emptyGuardianLinkForm());
      setFormKey((current) => current + 1);
      const shown = form.approveNow ? 'approved' : 'pending';
      if (filter === shown) await load(shown, null);
      else setFilter(shown);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
      // Keep only the athletes still to link; a guardian created on the way now exists.
      setForm(
        createdGuardianPersonId === null
          ? remaining
          : {
              ...remaining,
              guardianSource: 'existing',
              guardianPersonId: createdGuardianPersonId,
              guardianLabel: `${form.givenName.trim()} ${form.familyName.trim()}`,
            },
      );
      await load(filter, null);
    }
  }

  /** Invites the guardian of a link to their own account; returns the link and the e-mail outcome. */
  async function inviteGuardian(
    link: GuardianLinkView,
    email: string,
  ): Promise<CreatedInvitationNotice | null> {
    setErrorKey(null);
    setSaved(false);
    try {
      const created = await apiRequest<CreatedInvitationResponse>(`${base}/invitations`, {
        method: 'POST',
        body: {
          recipientEmail: email.trim(),
          permissionBundles: ['guardian'],
          personId: link.guardianPersonId,
        },
        csrfToken,
      });
      return createdInvitationNotice(window.location.origin, created);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
      return null;
    }
  }

  return (
    <Box component="section" aria-labelledby="guardian-links-title" sx={{ mb: 3 }}>
      <Typography variant="h6" component="h2" id="guardian-links-title" gutterBottom>
        {translate(locale, 'guardianLinks.title')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {translate(locale, 'guardianLinks.intro')}
      </Typography>
      <TextField
        select
        size="small"
        label={translate(locale, 'guardianLinks.filter.label')}
        value={filter}
        onChange={(event) => {
          setLinks(null);
          setFilter(event.target.value as GuardianLinkFilter);
        }}
        sx={{ mb: 2, minWidth: 220 }}
      >
        {guardianLinkFilters.map((option) => (
          <MenuItem key={option} value={option}>
            {translate(locale, guardianLinkFilterKeys[option])}
          </MenuItem>
        ))}
      </TextField>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {saved && errorKey === null && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {translate(locale, 'guardianLinks.saved')}
        </Alert>
      )}
      <AdultTransitionsSection
        key={transitionsKey}
        locale={locale}
        base={base}
        csrfToken={csrfToken}
      />
      {links === null && errorKey === null && (
        <Typography>{translate(locale, 'guardianLinks.loading')}</Typography>
      )}
      {links?.length === 0 && <Typography>{translate(locale, 'guardianLinks.empty')}</Typography>}
      <Stack component="ul" spacing={1} sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {links?.map((link) => (
          <GuardianLinkRow
            key={link.id}
            locale={locale}
            link={link}
            confirmingRevoke={confirmingRevoke === link.id}
            onAskRevoke={() => {
              setConfirmingRevoke(link.id);
            }}
            onDecide={(decision) => decide(link, decision)}
            onAuthorizeAdult={() => authorizeAdult(link)}
            onUpdate={(capabilities, validUntil) => updateLink(link, capabilities, validUntil)}
            onInvite={(email) => inviteGuardian(link, email)}
          />
        ))}
      </Stack>
      {nextCursor !== null && (
        <Button
          sx={{ mt: 2 }}
          onClick={() => {
            void load(filter, nextCursor);
          }}
        >
          {translate(locale, 'guardianLinks.loadMore')}
        </Button>
      )}
      <Box
        component="form"
        sx={{ mt: 3 }}
        aria-labelledby="guardian-links-create-title"
        onSubmit={(event) => {
          event.preventDefault();
          void request();
        }}
      >
        <Typography
          variant="subtitle1"
          component="h3"
          id="guardian-links-create-title"
          gutterBottom
        >
          {translate(locale, 'guardianLinks.create.title')}
        </Typography>
        <Stack spacing={2}>
          <Typography color="text.secondary">
            {translate(locale, 'guardianLinks.create.athletesHint')}
          </Typography>
          <RemoteSelect
            key={`athlete-${String(formKey)}-${String(athletePickerKey)}`}
            label={translate(locale, 'guardianLinks.create.athleteSearch')}
            noMatchesText={translate(locale, 'guardianLinks.create.noMatches')}
            load={findAthletes}
            required={form.athletes.length === 0}
            onSelect={(id, label) => {
              if (id === '') return;
              setForm((current) => withAthlete(current, { id, label }));
              setAthletePickerKey((current) => current + 1);
            }}
          />
          {form.athletes.length > 0 && (
            <Box
              component="ul"
              aria-label={translate(locale, 'guardianLinks.create.chosenAthletes')}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                listStyle: 'none',
                padding: 0,
                margin: 0,
              }}
            >
              {form.athletes.map((athlete) => (
                <li key={athlete.id}>
                  <Chip
                    label={athlete.label}
                    onDelete={() => {
                      setForm((current) => withoutAthlete(current, athlete.id));
                    }}
                  />
                </li>
              ))}
            </Box>
          )}
          <GuardianSource
            locale={locale}
            form={form}
            formKey={formKey}
            findGuardians={findGuardians}
            onChange={setForm}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flexWrap: 'wrap' }}>
            <TextField
              select
              size="small"
              label={translate(locale, 'guardianLinks.relationship')}
              value={form.relationship}
              onChange={(event) => {
                setForm({
                  ...form,
                  relationship: event.target.value as GuardianLinkForm['relationship'],
                });
              }}
              sx={{ minWidth: 200 }}
            >
              {guardianRelationships.map((relationship) => (
                <MenuItem key={relationship} value={relationship}>
                  {translate(locale, guardianRelationshipKeys[relationship])}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              type="date"
              label={translate(locale, 'guardianLinks.validFrom')}
              value={form.validFrom}
              onChange={(event) => {
                setForm({ ...form, validFrom: event.target.value });
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <TextField
              size="small"
              type="date"
              label={translate(locale, 'guardianLinks.validUntil')}
              value={form.validUntil}
              onChange={(event) => {
                setForm({ ...form, validUntil: event.target.value });
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Stack>
          <Box component="fieldset" sx={{ border: 0, padding: 0, margin: 0 }}>
            <FormLabel component="legend">
              {translate(locale, 'guardianLinks.capabilities')}
            </FormLabel>
            <FormGroup>
              {guardianCapabilityNames.map((capability) => (
                <FormControlLabel
                  key={capability}
                  label={translate(locale, guardianCapabilityKeys[capability])}
                  control={
                    <Checkbox
                      checked={form.capabilities[capability]}
                      onChange={(event) => {
                        setForm({
                          ...form,
                          capabilities: {
                            ...form.capabilities,
                            [capability]: event.target.checked,
                          },
                        });
                      }}
                    />
                  }
                />
              ))}
            </FormGroup>
          </Box>
          <FormControlLabel
            label={translate(locale, 'guardianLinks.create.approveNow')}
            control={
              <Checkbox
                checked={form.approveNow}
                onChange={(event) => {
                  setForm({ ...form, approveNow: event.target.checked });
                }}
              />
            }
          />
          <Box>
            <Button type="submit" variant="contained" disabled={!canSubmitGuardianLink(form)}>
              {translate(locale, 'guardianLinks.create.submit')}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}

interface GuardianSourceProps {
  locale: SupportedLocale;
  form: GuardianLinkForm;
  formKey: number;
  findGuardians: (text: string) => Promise<RemoteOption[]>;
  onChange: (form: GuardianLinkForm) => void;
}

/** The guardian is picked from the club's known guardians or described as a new person. */
function GuardianSource({
  locale,
  form,
  formKey,
  findGuardians,
  onChange,
}: GuardianSourceProps): React.JSX.Element {
  return (
    <Box component="fieldset" sx={{ border: 0, padding: 0, margin: 0 }}>
      <FormLabel component="legend">
        {translate(locale, 'guardianLinks.create.guardianSource')}
      </FormLabel>
      <RadioGroup
        row
        value={form.guardianSource}
        onChange={(event) => {
          onChange({
            ...form,
            guardianSource: event.target.value as GuardianLinkForm['guardianSource'],
            // The picker starts empty again, so a guardian chosen earlier must not stay selected.
            guardianPersonId: '',
            guardianLabel: '',
          });
        }}
      >
        <FormControlLabel
          value="existing"
          control={<Radio />}
          label={translate(locale, 'guardianLinks.create.existingGuardian')}
        />
        <FormControlLabel
          value="new"
          control={<Radio />}
          label={translate(locale, 'guardianLinks.create.newGuardian')}
        />
      </RadioGroup>
      {form.guardianSource === 'existing' ? (
        <>
          <RemoteSelect
            key={`guardian-${String(formKey)}`}
            label={translate(locale, 'guardianLinks.create.guardianSearch')}
            noMatchesText={translate(locale, 'guardianLinks.create.noMatches')}
            load={findGuardians}
            required={form.guardianPersonId === ''}
            onSelect={(guardianPersonId, guardianLabel) => {
              onChange({ ...form, guardianPersonId, guardianLabel });
            }}
          />
          {form.guardianPersonId !== '' && form.guardianLabel !== '' && (
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              {translate(locale, 'guardianLinks.create.chosenGuardian')}: {form.guardianLabel}
            </Typography>
          )}
        </>
      ) : (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flexWrap: 'wrap' }}>
          <TextField
            size="small"
            required
            label={translate(locale, 'athletes.givenName')}
            value={form.givenName}
            onChange={(event) => {
              onChange({ ...form, givenName: event.target.value });
            }}
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
          <TextField
            size="small"
            required
            label={translate(locale, 'athletes.familyName')}
            value={form.familyName}
            onChange={(event) => {
              onChange({ ...form, familyName: event.target.value });
            }}
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
          <TextField
            size="small"
            type="email"
            label={translate(locale, 'athletes.email')}
            value={form.email}
            onChange={(event) => {
              onChange({ ...form, email: event.target.value });
            }}
            slotProps={{ htmlInput: { maxLength: 320 } }}
          />
          <TextField
            size="small"
            type="tel"
            label={translate(locale, 'athletes.phone')}
            value={form.phone}
            onChange={(event) => {
              onChange({ ...form, phone: event.target.value });
            }}
            slotProps={{ htmlInput: { maxLength: 40 } }}
          />
        </Stack>
      )}
    </Box>
  );
}

interface GuardianLinkRowProps {
  locale: SupportedLocale;
  link: GuardianLinkView;
  confirmingRevoke: boolean;
  onAskRevoke: () => void;
  onDecide: (decision: GuardianLinkDecisionName) => Promise<void>;
  onAuthorizeAdult: () => Promise<void>;
  onUpdate: (
    capabilities: Record<GuardianCapabilityName, boolean>,
    validUntil: string,
  ) => Promise<void>;
  /** Creates a guardian invitation for this link's guardian; null when it failed. */
  onInvite: (email: string) => Promise<CreatedInvitationNotice | null>;
}

function GuardianLinkRow({
  locale,
  link,
  confirmingRevoke,
  onAskRevoke,
  onDecide,
  onAuthorizeAdult,
  onUpdate,
  onInvite,
}: GuardianLinkRowProps): React.JSX.Element {
  const [editing, setEditing] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState(link.guardianEmail ?? '');
  const [invitedLink, setInvitedLink] = useState<CreatedInvitationNotice | null>(null);
  const canInvite =
    !link.guardianHasAccount && (link.status === 'pending' || link.status === 'approved');
  const [capabilities, setCapabilities] = useState<Record<GuardianCapabilityName, boolean>>(
    link.capabilities,
  );
  const [validUntil, setValidUntil] = useState(link.validUntil ?? '');
  const athleteName = athleteDisplayName(
    { givenName: link.athleteGivenName, familyName: link.athleteFamilyName },
    locale,
  );
  const guardianName = athleteDisplayName(
    { givenName: link.guardianGivenName, familyName: link.guardianFamilyName },
    locale,
  );
  const granted = guardianCapabilityNames
    .filter((capability) => link.capabilities[capability])
    .map((capability) => translate(locale, guardianCapabilityKeys[capability]));
  return (
    <Box
      component="li"
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, padding: 2 }}
      aria-label={`${guardianName} – ${athleteName}`}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <Typography component="h3" variant="subtitle1" sx={{ flexGrow: 1 }}>
          {guardianName} → {athleteName}
        </Typography>
        <Chip
          size="small"
          label={translate(locale, guardianLinkStatusKeys[link.status])}
          color={
            link.status === 'approved'
              ? 'success'
              : link.status === 'pending'
                ? 'warning'
                : 'default'
          }
        />
      </Box>
      <Typography color="text.secondary">
        {translate(locale, 'guardianLinks.relationship')}:{' '}
        {translate(locale, guardianRelationshipKeys[link.relationship])}
      </Typography>
      <Typography color="text.secondary">
        {translate(locale, 'guardianLinks.validFrom')}: {formatCalendarDate(link.validFrom, locale)}
        {' · '}
        {translate(locale, 'guardianLinks.validUntil')}:{' '}
        {link.validUntil === null
          ? translate(locale, 'guardianLinks.openEnded')
          : formatCalendarDate(link.validUntil, locale)}
      </Typography>
      {link.guardianEmail !== null && (
        <Typography color="text.secondary">
          {translate(locale, 'athletes.email')}: {link.guardianEmail}
        </Typography>
      )}
      {link.guardianPhone !== null && (
        <Typography color="text.secondary">
          {translate(locale, 'athletes.phone')}: {link.guardianPhone}
        </Typography>
      )}
      {granted.length > 0 && (
        <Typography color="text.secondary">
          {translate(locale, 'guardianLinks.capabilities')}: {granted.join(', ')}
        </Typography>
      )}
      {link.suspendedForAdult && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {translate(locale, 'guardianLinks.adult.suspended')}{' '}
          {formatCalendarDate(link.athleteAdultOn, locale)}
          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                void onAuthorizeAdult();
              }}
            >
              {translate(locale, 'guardianLinks.adult.renew')}
            </Button>
          </Box>
        </Alert>
      )}
      {link.adultAuthorizedOn !== null && (
        <Typography color="text.secondary">
          {translate(locale, 'guardianLinks.adult.renewedOn')}{' '}
          {formatCalendarDate(link.adultAuthorizedOn, locale)}
        </Typography>
      )}
      {editing && (
        <Box
          component="form"
          sx={{ mt: 1 }}
          onSubmit={(event) => {
            event.preventDefault();
            void onUpdate(capabilities, validUntil).then(() => {
              setEditing(false);
            });
          }}
        >
          <FormGroup>
            {guardianCapabilityNames.map((capability) => (
              <FormControlLabel
                key={capability}
                label={translate(locale, guardianCapabilityKeys[capability])}
                control={
                  <Checkbox
                    checked={capabilities[capability]}
                    onChange={(event) => {
                      setCapabilities({ ...capabilities, [capability]: event.target.checked });
                    }}
                  />
                }
              />
            ))}
          </FormGroup>
          <TextField
            size="small"
            type="date"
            sx={{ mt: 1 }}
            label={translate(locale, 'guardianLinks.validUntil')}
            value={validUntil}
            onChange={(event) => {
              setValidUntil(event.target.value);
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button type="submit" variant="contained" size="small">
              {translate(locale, 'guardianLinks.edit.save')}
            </Button>
            <Button
              size="small"
              onClick={() => {
                setEditing(false);
              }}
            >
              {translate(locale, 'guardianLinks.edit.cancel')}
            </Button>
          </Stack>
        </Box>
      )}
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        {availableDecisions(link.status).map((decision) =>
          decision === 'revoke' && !confirmingRevoke ? (
            <Button key={decision} size="small" color="warning" onClick={onAskRevoke}>
              {translate(locale, decisionLabelKeys.revoke)}
            </Button>
          ) : (
            <Button
              key={decision}
              size="small"
              variant={decision === 'approve' ? 'contained' : 'outlined'}
              color={decision === 'approve' ? 'primary' : 'warning'}
              onClick={() => {
                void onDecide(decision);
              }}
            >
              {translate(
                locale,
                decision === 'revoke'
                  ? 'guardianLinks.revoke.confirm'
                  : decisionLabelKeys[decision],
              )}
            </Button>
          ),
        )}
        {(link.status === 'pending' || link.status === 'approved') && !editing && (
          <Button
            size="small"
            onClick={() => {
              setCapabilities(link.capabilities);
              setValidUntil(link.validUntil ?? '');
              setEditing(true);
            }}
          >
            {translate(locale, 'guardianLinks.edit.open')}
          </Button>
        )}
        {canInvite && !inviting && invitedLink === null && (
          <Button
            size="small"
            onClick={() => {
              setInviting(true);
            }}
          >
            {translate(locale, 'guardianLinks.invite.open')}
          </Button>
        )}
      </Stack>
      {inviting && (
        <Stack
          component="form"
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ mt: 2 }}
          onSubmit={(event) => {
            event.preventDefault();
            void onInvite(inviteEmail).then((created) => {
              if (created === null) return;
              setInvitedLink(created);
              setInviting(false);
            });
          }}
        >
          <TextField
            required
            size="small"
            type="email"
            label={translate(locale, 'invitations.create.email')}
            helperText={translate(locale, 'guardianLinks.invite.hint')}
            value={inviteEmail}
            onChange={(event) => {
              setInviteEmail(event.target.value);
            }}
            sx={{ minWidth: 260 }}
          />
          <Box>
            <Button type="submit" variant="contained">
              {translate(locale, 'invitations.create.submit')}
            </Button>
          </Box>
          <Box>
            <Button
              onClick={() => {
                setInviting(false);
              }}
            >
              {translate(locale, 'guardianLinks.edit.cancel')}
            </Button>
          </Box>
        </Stack>
      )}
      {invitedLink !== null && (
        <Box sx={{ mt: 2 }}>
          <InvitationLinkNotice locale={locale} notice={invitedLink} />
        </Box>
      )}
    </Box>
  );
}

interface AdultTransitionsSectionProps {
  locale: SupportedLocale;
  base: string;
  csrfToken: string;
}

/**
 * Athletes turning eighteen within a month, or who did in the last weeks: their guardians' access is
 * suspended from the birthday until a secretary renews it, and the athlete can be invited to take control.
 */
function AdultTransitionsSection({
  locale,
  base,
  csrfToken,
}: AdultTransitionsSectionProps): React.JSX.Element | null {
  const [items, setItems] = useState<AdultTransitionView[] | null>(null);
  const [inviting, setInviting] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [createdLink, setCreatedLink] = useState<CreatedInvitationNotice | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);

  useEffect(() => {
    apiRequest<AdultTransitionView[]>(`${base}/guardian-links/adult-transitions`).then(
      setItems,
      () => {
        setItems([]);
      },
    );
  }, [base]);

  async function invite(item: AdultTransitionView): Promise<void> {
    setErrorKey(null);
    try {
      const created = await apiRequest<CreatedInvitationResponse>(`${base}/invitations`, {
        method: 'POST',
        body: {
          recipientEmail: email.trim(),
          permissionBundles: ['athlete'],
          personId: item.personId,
        },
        csrfToken,
      });
      setCreatedLink(createdInvitationNotice(window.location.origin, created));
      setInviting(null);
      setEmail('');
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }

  if (items === null || items.length === 0) return null;
  return (
    <Box
      component="section"
      aria-label={translate(locale, 'guardianLinks.adult.title')}
      sx={{ mb: 3 }}
    >
      <Typography variant="h6" component="h3" gutterBottom>
        {translate(locale, 'guardianLinks.adult.title')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 1 }}>
        {translate(locale, 'guardianLinks.adult.intro')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {createdLink !== null && <InvitationLinkNotice locale={locale} notice={createdLink} />}
      <Stack component="ul" spacing={1} sx={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {items.map((item) => (
          <Box
            component="li"
            key={item.athleteId}
            sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}
          >
            <Typography sx={{ fontWeight: 500 }}>{athleteDisplayName(item, locale)}</Typography>
            <Chip
              size="small"
              color={item.daysUntilAdult > 0 ? 'info' : 'warning'}
              label={`${translate(locale, 'guardianLinks.adult.on')} ${formatCalendarDate(item.adultOn, locale)} (${String(item.daysUntilAdult)} ${translate(locale, 'guardianLinks.adult.days')})`}
            />
            <Typography variant="body2" color="text.secondary">
              {translate(locale, 'guardianLinks.adult.awaiting')}: {item.linksAwaitingAuthorization}
              {' · '}
              {translate(locale, 'guardianLinks.adult.renewed')}: {item.linksAuthorized}
            </Typography>
            {item.hasAccount ? (
              <Chip
                size="small"
                variant="outlined"
                label={translate(locale, 'guardianLinks.adult.hasAccount')}
              />
            ) : (
              <Button
                size="small"
                onClick={() => {
                  setInviting(inviting === item.athleteId ? null : item.athleteId);
                  setCreatedLink(null);
                }}
              >
                {translate(locale, 'guardianLinks.adult.invite')}
              </Button>
            )}
            {inviting === item.athleteId && (
              <Box
                component="form"
                sx={{ display: 'flex', gap: 1, width: '100%' }}
                onSubmit={(event) => {
                  event.preventDefault();
                  void invite(item);
                }}
              >
                <TextField
                  size="small"
                  type="email"
                  required
                  label={translate(locale, 'invitations.create.email')}
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                  }}
                />
                <Button type="submit" variant="contained" size="small">
                  {translate(locale, 'invitations.create.submit')}
                </Button>
              </Box>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
